import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
} from "@fluentui/react-components";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useClientBalancePageStore } from "store/ClientBalancePageStore";

import InfoEntriesGrid from "./InfoEntriesGrid";
import PaymentsByClientSaldoEntryGrid from "./PaymentsByClientSaldoEntryGrid";
import { VSpace } from "components/Spacer";
import { compareNumbersUpToTwoDecimals, currencyFormat } from "utils/utils";
import { useAppStore } from "store/AppStore";
import { formatNumber } from "utils/number";
import { DocumentTableRegular } from "@fluentui/react-icons";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { ExportExcel } from "./ExportExcel";
import { PaymentsByClientSaldoEntryExportExcel } from "./PaymentsByClientSaldoEntryExportExcel";

export const InfoClearanceDialog = () => {
  const [
    {
      clientBalances,
      infoClearanceDialog,
      infoClearanceBalanceEntryStatus,
      infoSettledPayments,
      selectedClientSaldoEntryPaymentId,
      paymentsByClientSaldoEntry,
      paymentsByClientSaldoEntryStatus,
    },
    { setClientBalancePageStoreValues },
  ] = useClientBalancePageStore();

  const refExcelExport = useRef<ExcelExport>();
  const refExcelExport2 = useRef<ExcelExport>();

  const [clTotal, setClTotal] = React.useState(0);

  const isLoading = infoClearanceBalanceEntryStatus === "loading";
  const { t } = useTranslation();

  const saveExcel = (component) => {
    if (!component) return;

    const options = component.workbookOptions();
    const data = component.props.data;
    const rows = options.sheets[0].rows;
    if (rows.length === 0) return;
    if (data.length === 0) return;
    for (let index = 1; index < rows.length; index++) {
      const item = data[index - 1];
      const row = rows[index];
      if (row.type === "data" && item["Status"] === "2") {
        row.cells.forEach((cell) => {
          cell.background = "#fed9cc";
        });
      }
    }
    component.save(options);
  };
  useEffect(() => {
    const clientBalance = clientBalances.find((el) => {
      return el.bankStatementEntryId === infoClearanceDialog.clientBalanceId;
    });
    setClientBalancePageStoreValues({
      clientBalance: clientBalance,
    });
  }, [clientBalances, infoClearanceDialog.clientBalanceId]);

  useEffect(() => {
    if (!infoSettledPayments) {
      return;
    }
    const totalInfoAmount = infoSettledPayments.reduce(
      (acc, el, currentIndex, array) => {
        if (currentIndex !== array.length) {
          acc = acc + (el.premiumAmount ?? 0);
        }
        return acc;
      },
      0
    );
    setClTotal(totalInfoAmount);
  }, [infoSettledPayments]);

  const clientName = useMemo(() => {
    const balance = clientBalances.find((el) => {
      return el.clientBalanceId === infoClearanceDialog.clientBalanceId;
    });
    return balance?.clientName;
  }, [clientBalances, infoClearanceDialog]);

  return (
    <>
      <Dialog open={infoClearanceDialog.isOpen}>
        <DialogSurface
          data-help="client-balance-info-dialog"
          style={{ maxWidth: "96%", transform: "none" }}
        >
          <DialogBody>
            <DialogTitle>
              {t("pct.infoClearanceBalanceEntry.label") + " - " + clientName}
            </DialogTitle>
            <DialogContent>
              {infoClearanceBalanceEntryStatus !== "loading" ? (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    {t("pct.clientBalanceEntries.label")}
                  </div>
                  <InfoEntriesGrid />
                  {selectedClientSaldoEntryPaymentId != null ? (
                    <>
                      <VSpace height={20} />
                      <hr />
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>
                        {t("pct.paymentsByClientSaldoEntry.label")}
                      </div>
                      {paymentsByClientSaldoEntryStatus === "loading" &&
                      paymentsByClientSaldoEntry.length === 0 ? (
                        <>
                          <VSpace height={100} />
                          <Spinner size="large" />
                          <VSpace height={100} />
                        </>
                      ) : paymentsByClientSaldoEntryStatus === "success" &&
                        paymentsByClientSaldoEntry.filter(
                          (el) => !el.isDeleted
                        ).length === 0 ? (
                        <div
                          style={{
                            padding: "16px",
                            textAlign: "center",
                            opacity: 0.7,
                          }}
                        >
                          {t("greco.noData")}
                        </div>
                      ) : (
                        <PaymentsByClientSaldoEntryGrid />
                      )}
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <VSpace height={175} />
                  <Spinner size="large" />
                  <VSpace height={175} />
                </>
              )}
            </DialogContent>
            <DialogActions>
              {infoSettledPayments.filter((el) => !el.isDeleted).length > 0 &&
              !isLoading ? (
                <Button
                  data-help="client-balance-info-export"
                  appearance="primary"
                  disabled={isLoading}
                  icon={<DocumentTableRegular />}
                  onClick={() => {
                    saveExcel(refExcelExport.current);
                  }}
                >
                  {t("greco.exportExcel")}
                </Button>
              ) : null}
              {selectedClientSaldoEntryPaymentId != null &&
              paymentsByClientSaldoEntryStatus !== "loading" &&
              paymentsByClientSaldoEntry.filter((el) => !el.isDeleted).length >
                0 ? (
                <Button
                  data-help="client-balance-info-export-payments"
                  appearance="primary"
                  disabled={isLoading}
                  icon={<DocumentTableRegular />}
                  onClick={() => {
                    saveExcel(refExcelExport2.current);
                  }}
                >
                  {t("pct.exportExcelPaymentsByClientSaldoEntry.label")}
                </Button>
              ) : null}
              <Button
                data-help="client-balance-info-close"
                appearance="secondary"
                disabled={isLoading}
                onClick={() => {
                  setClientBalancePageStoreValues({
                    infoClearanceDialog: {
                      isOpen: false,
                      clientBalanceId: null,
                    },
                    selectedClientSaldoEntryPaymentId: null,
                    paymentsByClientSaldoEntry: [],
                    paymentsByClientSaldoEntryMetadataMap: {},
                    paymentsByClientSaldoEntryStatus: "none",
                  });
                }}
              >
                {t("pct.close.label")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <ExportExcel ref={refExcelExport} />
      <PaymentsByClientSaldoEntryExportExcel ref={refExcelExport2} />
    </>
  );
};
